// Stop-gap mapping from the persisted `TranslationHandler.id` (stamped by
// the backend translation providers — e.g. 'openai-simple-v1' from
// OpenAISimpleTranslationProvider, 'import-srt-v1' from
// ImportSRTTranslationProvider) to the modal's source-picker option id.
//
// Both 'openai-llm-v1' (legacy default) and 'openai-simple-v1' (current
// default per the modal's SOURCES wiring) collapse to the same modal source
// 'openai-llm' so replace-mode keeps recognizing the previous handler for
// existing on-disk translations. PLAT-9967 review: missing 'openai-simple-v1'
// silently broke the replace-mode "Currently using X" helper for newly
// generated translations.
//
// Display labels are NOT duplicated here — they're read off the modal's
// SOURCES constant so the helper / method-change copy uses the exact
// strings the user sees in the dropdown.
//
// PLAT-9780 / Story D move this to data on the DatoCMS TranslationHandler
// model. Until then, three entries cover everything we offer.

import type { TranslationData } from '@src/types.ts/translation';

/**
 * Available translation sources. Two for now — wired by hardcoded id +
 * label here so the picker ships ahead of [PLAT-9780](https://arborstory.atlassian.net/browse/PLAT-9780)
 * (which moves the catalog into DatoCMS via `TranslationHandler.displayName`).
 * When that ships, this `SOURCES` array becomes the read off the
 * `TranslationHandlerStore` and the `TranslationSourceId` union widens.
 */
export type TranslationSourceId = 'openai-llm' | 'srt-upload';

export const TRANSLATION_SOURCES: ReadonlyArray<{
  id: TranslationSourceId;
  label: string;
  /**
   * Handler internalName passed to the backend Generate endpoint when
   * this source is selected. Omitted for non-Generate sources (SRT routes
   * through its own endpoint with its own fixed handler).
   */
  handlerInternalName?: string;
}> = [
  {
    id: 'openai-llm',
    label: 'OpenAI LLM Translation',
    // Must stay 'openai-simple-v1': the backend's translateCompositions is
    // only implemented for the *-simple-* providers (openai-simple-v1 /
    // anthropic-simple-v1). 'openai-llm-v1' throws "not implemented", which
    // would break composition translation (PLAT-9870 epic). Backend default
    // is also 'openai-simple-v1' (resolveProvider.ts DEFAULT_HANDLER_ID).
    handlerInternalName: 'openai-simple-v1',
  },
  {
    id: 'srt-upload',
    label: 'SRT Upload (With Sentence Splitting)',
  },
];

export const HANDLER_ID_TO_SOURCE: Record<string, TranslationSourceId> = {
  'openai-llm-v1': 'openai-llm',
  'openai-simple-v1': 'openai-llm',
  'import-srt-v1': 'srt-upload',
};

/**
 * Short source label for the Translation Review Summary's "Source" row —
 * the LLM-vs-SRT category (PLAT-10444), distinct from the specific engine the
 * "Handler" row shows (e.g. `openai-simple-v1`).
 */
export const TRANSLATION_SOURCE_SHORT_LABEL: Record<
  TranslationSourceId,
  string
> = {
  'openai-llm': 'LLM',
  'srt-upload': 'SRT',
};

export interface ResolvedPreviousSource {
  sourceId: TranslationSourceId;
  label: string;
}

export function resolvePreviousSource(
  data: TranslationData | undefined,
): ResolvedPreviousSource | null {
  const handlerId = data?.handler?.id;
  if (!handlerId) return null;
  const sourceId = HANDLER_ID_TO_SOURCE[handlerId];
  if (!sourceId) return null;
  const label = TRANSLATION_SOURCES.find(s => s.id === sourceId)?.label;
  return label ? { sourceId, label } : null;
}
